"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, Cpu, Layers, ShieldCheck, Sparkles, Workflow } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
interface TechPartner {
  id: string;
  name: string;
  category: string;
  description: string;
  syncType: string;
  featured: boolean;
}
const techPartners: TechPartner[] = [{
  id: "siteminder",
  name: "SiteMinder",
  category: "Channel Manager",
  description: "Two-way inventory distribution and automated rate plan parity across 450+ global booking channels.",
  syncType: "Instant 2-way sync",
  featured: true
}, {
  id: "rategain",
  name: "RateGain",
  category: "Rate Intelligence & Distribution",
  description: "Real-time competitive pricing intelligence, dynamic parity tracking, and automated market rate alerts.",
  syncType: "Live rate feed",
  featured: true
}, {
  id: "staah",
  name: "STAAH",
  category: "Channel Management",
  description: "Centralized inventory control, instant booking engine updates, and regional OTA channel synchronization.",
  syncType: "Real-time mapping",
  featured: false
}, {
  id: "axisrooms",
  name: "AxisRooms",
  category: "Channel & Revenue Suite",
  description: "Multi-property rate allocation, dynamic inventory distribution, and unified domestic OTA management.",
  syncType: "Cloud API sync",
  featured: false
}, {
  id: "ezee",
  name: "eZee Technosys",
  category: "PMS & Distribution",
  description: "Seamless front-desk PMS integration, direct billing sync, and automated room categorization updates.",
  syncType: "Native integration",
  featured: false
}, {
  id: "cloudbeds",
  name: "Cloudbeds",
  category: "Hospitality Management",
  description: "Unified cloud property management, automated guest folio linkage, and cross-channel reservation routing.",
  syncType: "REST API sync",
  featured: true
}, {
  id: "opera",
  name: "Oracle Opera PMS",
  category: "Enterprise PMS",
  description: "Enterprise revenue management extraction, historical folio analysis, and custom yield parameter imports.",
  syncType: "Enterprise connector",
  featured: true
}];
const highlights = [{
  id: "zero-downtime",
  title: "Zero operational downtime",
  desc: "Deploy rate optimization rules directly into your active platforms without interrupting live guest bookings."
}, {
  id: "data-security",
  title: "Enterprise data isolation",
  desc: "Strict read-write access protocols that preserve guest privacy and safeguard proprietary commercial data."
}, {
  id: "custom-mapping",
  title: "Custom room-rate mapping",
  desc: "Granular room category and non-refundable rate plan linking mapped to your exact revenue structure."
}];
export default function Integrations() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="techstackintegrations" data-nav="light" className="relative overflow-hidden bg-background py-20 md:py-28">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 border border-secondary/30 bg-secondary/10 px-3.5 py-1 text-xs font-medium uppercase tracking-wider text-secondary">
            <Cpu className="size-3.5" />
            Ecosystem compatibility
          </div>

          <h2 className="mt-4 font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
            Seamless compatibility with your tech stack
          </h2>

          <p className="mt-4 text-base text-muted-foreground sm:text-lg">
            Connect your existing PMS, channel manager, and rate intelligence tools without changing your daily front-desk operations.
          </p>
        </div>

        {/* Tech Grid */}
        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {techPartners.map((partner, index) => <motion.div key={partner.id} data-index={index} initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.1
        }} transition={{
          duration: 0.4,
          delay: index * 0.05
        }} className={partner.name === "Oracle Opera PMS" ? "sm:col-span-2 lg:col-span-1 xl:col-span-2" : ""}>
              <Card className="group relative flex h-full flex-col justify-between rounded-xl border border-border bg-card p-6 transition-[border-color,box-shadow] duration-200 hover:border-secondary/50 hover:shadow-md">
                <div>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="flex size-11 items-center justify-center rounded-lg border border-border bg-muted/50 font-serif text-lg font-bold text-foreground transition-colors group-hover:border-secondary/40 group-hover:bg-secondary/10 group-hover:text-secondary">
                        {partner.name.charAt(0)}
                      </div>
                      <div>
                        <CardTitle className="font-serif text-lg font-semibold text-card-foreground">
                          {partner.name}
                        </CardTitle>
                        <p className="text-xs text-muted-foreground">
                          {partner.category}
                        </p>
                      </div>
                    </div>

                    {partner.featured && <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/15 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
                        Core partner
                      </Badge>}
                  </div>

                  <CardDescription className="mt-4 text-xs leading-relaxed text-muted-foreground sm:text-sm">
                    {partner.description}
                  </CardDescription>
                </div>

                <div className="mt-6 flex items-center justify-between border-t border-border pt-4 text-xs">
                  <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
                    <CheckCircle2 className="size-3.5 text-emerald-500" />
                    {partner.syncType}
                  </span>
                  <span className="text-muted-foreground">Verified link</span>
                </div>
              </Card>
            </motion.div>)}
        </div>

        {/* Feature Highlights Strip */}
        <div className="mt-14 border border-border bg-muted/40 p-6 md:p-8">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {highlights.map((item, i) => <div key={item.id} data-index={i} className="flex items-start gap-3.5 border-b border-border/60 pb-6 last:border-b-0 md:border-b-0 md:border-r md:border-border/60 md:pb-0 md:pr-6 md:last:border-r-0">
                <div className="mt-1 flex size-7 shrink-0 items-center justify-center border border-secondary/30 bg-secondary/10 text-secondary">
                  <Workflow className="size-4" />
                </div>
                <div>
                  <h3 className="font-serif text-base font-semibold text-foreground">
                    {item.title}
                  </h3>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                    {item.desc}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        {/* Action Callout */}
        <div className="mt-12 flex flex-col items-center justify-between gap-6 border border-border bg-card p-6 text-center sm:flex-row sm:text-left md:p-8">
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-secondary">
              <ShieldCheck className="size-4" />
              Custom hospitality setups supported
            </div>
            <h3 className="mt-1 font-serif text-xl font-bold text-foreground sm:text-2xl">
              Using a proprietary or niche PMS system?
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Sunil Singh evaluates your setup during the initial audit and builds custom data pipelines for reliable rate execution.
            </p>
          </div>

          <div className="shrink-0">
            <Button asChild className="rounded-full bg-secondary text-primary-foreground hover:bg-secondary/90">
              <Link to="#" className="inline-flex items-center gap-2">
                <span>Check your system compatibility</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}